import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { UtensilsCrossed, MapPin, FileText, Building2, CheckCircle2, Loader2, LogOut } from "lucide-react";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import SEOHead from "../components/SEOHead";

// Onboarding restaurateur Nold Go — normes belges (TVA BCE + autorisation AFSCA).
// Bilingue via i18next : namespace "restaurateur".
// Alias SEO : /partenaires, /partenaire, /devenir-partenaire, /nold-partenaire
export default function RestaurateurOnboarding() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ businessName: "", address: "", postalCode: "", city: "Bruxelles", vatNumber: "", phone: "", cuisine: "" });
  const [afscaDoc, setAfscaDoc] = useState("");
  const [status, setStatus] = useState("not_submitted");
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let ok = true;
    (async () => {
      // Sur ce formulaire public, on ne charge le dossier existant QUE si l'utilisateur est connecté.
      if (!user) {
        // Restauration éventuelle du brouillon posé avant login.
        try {
          const draft = JSON.parse(sessionStorage.getItem("bsg_rt_draft") || "null");
          if (draft) {
            setState((s) => ({ ...s, businessName: draft.businessName || "", address: draft.address || "", postalCode: draft.postalCode || "", city: draft.city || "Bruxelles", vatNumber: draft.vatNumber || draft.vat || "", phone: draft.phone || "", cuisine: draft.cuisine || "" }));
            if (draft.afscaDoc) setAfscaDoc(draft.afscaDoc);
          }
        } catch { /* ignore */ }
        setLoading(false);
        return;
      }
      setLoading(true);
      try {
        const { data } = await api.get("/restaurateur/me");
        if (!ok) return;
        setStatus(data.status || "not_submitted");
        if (data.dossier) {
          setState((s) => ({
            ...s,
            businessName: data.dossier.business_name || "",
            address: data.dossier.address || "",
            postalCode: data.dossier.postal_code || "",
            city: data.dossier.city || "Bruxelles",
            vatNumber: data.dossier.vat_number || "",
            phone: data.dossier.phone || "",
            cuisine: data.dossier.cuisine || "",
          }));
          setAfscaDoc(data.dossier.afsca_doc || "");
        }
      } catch (e) { /* silencieux */ }
      finally { if (ok) setLoading(false); }
    })();
    return () => { ok = false; };
  }, [user]);

  // Si l'utilisateur est déjà approuvé, on redirige vers son dashboard.
  useEffect(() => {
    if (status === "approved") navigate("/restaurateur", { replace: true });
  }, [status, navigate]);

  const submit = async () => {
    if (!state.businessName.trim() || state.businessName.trim().length < 2) return toast.error(t("restaurateur.err.name"));
    if (!state.address.trim() || state.address.trim().length < 5) return toast.error(t("restaurateur.err.address"));
    const vat = state.vatNumber.replace(/[\s.]/g, "").toUpperCase();
    const vatOk = /^(BE)?[01]\d{9}$/.test(vat);
    if (!vatOk) return toast.error(t("restaurateur.err.vat"));
    // AFSCA supprimé du formulaire — plus de check ici.
    if (!user) {
      try {
        sessionStorage.setItem("bsg_rt_draft", JSON.stringify({ ...state, vat }));
      } catch { /* ignore */ }
      toast.info(t("restaurateur.needLogin"));
      navigate("/auth?role=restaurateur");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/restaurateur/onboarding", {
        business_name: state.businessName.trim(),
        address: state.address.trim(),
        postal_code: state.postalCode.trim() || null,
        city: state.city.trim() || "Bruxelles",
        vat_number: vat,
        phone: state.phone.trim() || null,
        cuisine: state.cuisine.trim() || null,
      }, { timeout: 60000 });
      setStatus("pending");
      toast.success(t("restaurateur.submitted"));
    } catch (e) {
      // Messages explicites pour l'utilisateur — utile debug mobile Android.
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (e?.code === "ECONNABORTED" || e?.message?.includes("timeout")) {
        toast.error("Envoi trop long — vérifiez votre connexion et réessayez.");
      } else if (status === 413) {
        toast.error("Document trop volumineux. Reprenez la photo en approchant du document.");
      } else if (!navigator.onLine) {
        toast.error("Vous êtes hors ligne. Reconnectez-vous puis réessayez.");
      } else {
        toast.error(detail || apiError(e) || "Erreur d'envoi. Réessayez.");
      }
    }
    finally { setSubmitting(false); }
  };

  // Compression client-side d'une image avant upload (indispensable Android — les photos JPG font souvent 5-8 MB).
  // Retourne le File d'origine si c'est un PDF ou si la compression échoue.
  const _compressImageIfNeeded = async (file) => {
    if (!file.type.startsWith("image/")) return file;
    try {
      const bitmap = await createImageBitmap(file);
      const MAX = 1600;
      let w = bitmap.width, h = bitmap.height;
      if (w > MAX || h > MAX) {
        const r = Math.min(MAX / w, MAX / h);
        w = Math.round(w * r); h = Math.round(h * r);
      }
      const canvas = document.createElement("canvas");
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(bitmap, 0, 0, w, h);
      const blob = await new Promise((res) => canvas.toBlob(res, "image/jpeg", 0.82));
      if (!blob) return file;
      // Ne remplace que si effectivement plus petit (sinon garde l'original)
      return blob.size < file.size ? blob : file;
    } catch { return file; }
  };

  // Lecture data-URL avec compression + timeout — remplace la lecture brute pour supporter Android.
  const _readAsDataUrl = (file) => new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ""));
    reader.onerror = () => reject(new Error("read"));
    reader.onabort = () => reject(new Error("abort"));
    reader.readAsDataURL(file);
  });

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#F26419]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#FDF8F0]" data-testid="restaurateur-onboarding">
      <SEOHead
        title="Devenir Partenaire Nold Go · Livraison de repas à Bruxelles"
        description="Rejoignez Nold Go et boostez vos ventes à Bruxelles. Livraison express — 0€ d'abonnement, seulement 25% de commission. Inscription en ligne, validation en 24 h."
        canonical="/partenaires"
        type="website"
        jsonLd={{
          "@context": "https://schema.org",
          "@type": "Offer",
          name: "Programme Partenaire Nold Go",
          description: "Rejoignez Nold Go et boostez vos ventes à Bruxelles. 0€ d'abonnement, 25% de commission.",
          url: "https://www.noldgo.com/partenaires",
          seller: {
            "@type": "Organization",
            name: "Nold Go SRL",
            url: "https://www.noldgo.com",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Rue Jourdan 71 A0",
              postalCode: "1060",
              addressLocality: "Saint-Gilles",
              addressCountry: "BE",
            },
          },
          eligibleRegion: { "@type": "AdministrativeArea", name: "Bruxelles-Capitale" },
          priceSpecification: {
            "@type": "UnitPriceSpecification",
            price: "0.00",
            priceCurrency: "EUR",
            unitText: "MONTH",
            description: "0€ d'abonnement — 25% de commission uniquement sur les ventes livrées.",
          },
        }}
      />
      <div className="max-w-2xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <div className="text-xs text-[#75807A] font-semibold uppercase tracking-wider">{t("restaurateur.tagline")}</div>
            <h1 className="mt-1 font-head font-black text-3xl sm:text-4xl text-[#1A1A1A]">{t("restaurateur.title")}</h1>
          </div>
          <button onClick={() => navigate("/")} className="text-[#75807A] hover:text-[#1A1A1A] text-sm font-medium inline-flex items-center gap-1"><LogOut className="w-4 h-4" /> {t("common.back")}</button>
        </div>

        {status === "pending" ? (
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="bg-white rounded-3xl border border-[#F26419]/30 p-8 text-center" data-testid="restaurateur-pending">
            <div className="w-16 h-16 mx-auto rounded-full bg-[#F26419]/10 flex items-center justify-center"><CheckCircle2 className="w-8 h-8 text-[#F26419]" /></div>
            <h2 className="mt-4 font-head font-bold text-2xl text-[#1A1A1A]">{t("restaurateur.pending.title")}</h2>
            <p className="mt-2 text-[#3B423E] text-sm">{t("restaurateur.pending.desc")}</p>
            <Button data-testid="restaurateur-go-dashboard" onClick={() => navigate("/restaurateur")} className="mt-6 h-12 rounded-xl bg-[#1A1A1A] hover:bg-black text-white font-semibold px-6">
              {t("restaurateur.pending.cta")}
            </Button>
          </motion.div>
        ) : (
          <>
            <div className="rounded-2xl bg-[#F26419]/10 border border-[#F26419]/30 p-4 mb-6 text-sm text-[#3B423E]" data-testid="restaurateur-intro">
              <p><strong>{t("restaurateur.intro.strong")}</strong> {t("restaurateur.intro.desc")}</p>
            </div>

            <div className="bg-white rounded-3xl border border-[#E2DFD8] p-6 space-y-5">
              <div>
                <label className="text-sm font-semibold text-[#3B423E] flex items-center gap-1.5"><Building2 className="w-4 h-4 text-[#F26419]" /> {t("restaurateur.field.name")}</label>
                <Input data-testid="rt-business-name" value={state.businessName} onChange={(e) => setState({ ...state, businessName: e.target.value })} placeholder={t("restaurateur.ph.name")} className="mt-1.5 h-12 rounded-xl border-2" />
              </div>

              <div>
                <label className="text-sm font-semibold text-[#3B423E] flex items-center gap-1.5"><MapPin className="w-4 h-4 text-[#F26419]" /> {t("restaurateur.field.address")}</label>
                <Input data-testid="rt-address" value={state.address} onChange={(e) => setState({ ...state, address: e.target.value })} placeholder={t("restaurateur.ph.address")} className="mt-1.5 h-12 rounded-xl border-2" />
                <div className="grid grid-cols-2 gap-3 mt-2">
                  <Input data-testid="rt-postal" value={state.postalCode} onChange={(e) => setState({ ...state, postalCode: e.target.value })} placeholder={t("restaurateur.ph.postal")} className="h-12 rounded-xl border-2" />
                  <Input data-testid="rt-city" value={state.city} onChange={(e) => setState({ ...state, city: e.target.value })} placeholder={t("restaurateur.ph.city")} className="h-12 rounded-xl border-2" />
                </div>
              </div>

              <div>
                <label className="text-sm font-semibold text-[#3B423E] flex items-center gap-1.5"><FileText className="w-4 h-4 text-[#F26419]" /> {t("restaurateur.field.vat")}</label>
                <Input data-testid="rt-vat" value={state.vatNumber} onChange={(e) => setState({ ...state, vatNumber: e.target.value })} placeholder="BE0XXX.XXX.XXX" className="mt-1.5 h-12 rounded-xl border-2 tracking-wider font-medium" />
                <p className="mt-1 text-xs text-[#75807A]">{t("restaurateur.hint.vat")}</p>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-semibold text-[#3B423E]">{t("restaurateur.field.phone")}</label>
                  <Input data-testid="rt-phone" value={state.phone} onChange={(e) => setState({ ...state, phone: e.target.value })} placeholder="+32 …" className="mt-1.5 h-12 rounded-xl border-2" />
                </div>
                <div>
                  <label className="text-sm font-semibold text-[#3B423E]">{t("restaurateur.field.cuisine")}</label>
                  <Input data-testid="rt-cuisine" value={state.cuisine} onChange={(e) => setState({ ...state, cuisine: e.target.value })} placeholder={t("restaurateur.ph.cuisine")} className="mt-1.5 h-12 rounded-xl border-2" />
                </div>
              </div>

              <Button
                data-testid="rt-submit"
                onClick={submit}
                disabled={submitting}
                className="w-full h-14 rounded-xl bg-[#F26419] hover:bg-[#D85312] text-white font-bold text-base disabled:opacity-50"
              >
                {submitting ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> {t("restaurateur.submitting")}</> : t("restaurateur.submit")}
              </Button>
              {status === "rejected" && (
                <div className="rounded-xl bg-red-50 border border-red-200 p-3 text-xs text-red-700" data-testid="rt-rejected">
                  {t("restaurateur.rejected")}
                </div>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
